import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import CheckIcon from "@mui/icons-material/Check";
import { Dialog } from "@/components/main/utils/Dialog";
import { DialogActions } from "@/components/main/utils/DialogActions";
import { Badge } from "components/common/Badge";
import { Button } from "components/common/Button";
import { LoadingState } from "components/common/LoadingState";
import { SelectionButton } from "components/common/SelectionButton";
import { TextInput } from "components/common/TextInput";
import { showNotificationGlobal } from "stores/useNotificationStore";
import {
  fetchManagerCandidates,
  grantSpaceRole,
  type ManagerCandidate,
  type OrgMember,
  type Space,
} from "@/lib/api/organizations";

interface AssignManagerDialogProps {
  open: boolean;
  space: Space;
  /// The org roster, so anyone who could hold the space is offerable — not
  /// only the few who already have a grant on it. Pending invitees are
  /// filtered out here: they hold no member tuple, so the door would refuse.
  members: OrgMember[];
  onClose: () => void;
  /// Fired after a successful grant so the caller can re-read the tree and the
  /// access list — the abandoned flag lives on the tree, not here.
  onAssigned: () => void;
}

/// Hand an abandoned space back to someone. The grant goes through the normal
/// `/spaces/grant` door (`can_manage_access` already admits an org governor);
/// all this adds is the shortlist of people who are already in the space, so
/// the org owner is not guessing at a roster they may not know.
export const AssignManagerDialog: React.FC<AssignManagerDialogProps> = ({
  open,
  space,
  members,
  onClose,
  onAssigned,
}) => {
  // undefined = loading, null = the fetch FAILED (unknown, not zero — an
  // empty array is a real answer and gets worded as one), array = loaded.
  const [candidates, setCandidates] = useState<
    ManagerCandidate[] | null | undefined
  >(undefined);
  const [selected, setSelected] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [assigning, setAssigning] = useState(false);

  const loadCandidates = useCallback(() => {
    setCandidates(undefined);
    void fetchManagerCandidates(space.space_id).then(setCandidates);
  }, [space.space_id]);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setEmail("");
    loadCandidates();
  }, [open, loadCandidates]);

  const selfId = localStorage.getItem("clientID");
  // Everyone the door would accept who is NOT already on the shortlist, so the
  // two lists never offer the same person twice.
  const otherMembers = useMemo(() => {
    const shortlisted = new Set((candidates ?? []).map((c) => c.client_id));
    return members.filter(
      (m) => !m.pending && m.email && !shortlisted.has(m.client_id),
    );
  }, [members, candidates]);

  // null is "the fetch failed" — unknown, never zero — so only a loaded empty
  // array may be worded as nobody holding a grant.
  const shortlist = Array.isArray(candidates) ? candidates : null;
  const emailLabel =
    otherMembers.length > 0 || (shortlist?.length ?? 0) > 0
      ? "Or by email"
      : shortlist?.length === 0
        ? "Nobody else holds a grant on this space — name a member by email."
        : "Grant a member by email";

  const assign = async () => {
    const target = selected ? { user: selected } : { email: email.trim() };
    setAssigning(true);
    try {
      await grantSpaceRole(space.space_id, target, "manager");
      showNotificationGlobal(`"${space.name}" has a manager again`, "success");
      onAssigned();
      onClose();
    } catch (error) {
      showNotificationGlobal(
        axios.isAxiosError(error) && typeof error.response?.data === "string"
          ? error.response.data
          : "Could not assign a manager",
        "error",
      );
    } finally {
      setAssigning(false);
    }
  };

  const personRow = (
    id: string,
    name: string,
    email: string,
    trailing?: React.ReactNode,
  ) => (
    <SelectionButton
      key={id}
      isSelected={selected === id}
      ariaPressed={selected === id}
      onClick={() => {
        setSelected(id);
        setEmail("");
      }}
      className="flex items-center gap-2 text-left"
    >
      <span className="min-w-0 flex-1 truncate">
        <span className="text-fg-regular">{name || email}</span>
        {id === selfId && (
          <span className="pl-1 text-fg-muted-regular">(You)</span>
        )}
        {name && <span className="pl-2 text-fg-muted-regular">{email}</span>}
      </span>
      {trailing}
      {selected === id && (
        <CheckIcon sx={{ fontSize: 12 }} className="text-accent" />
      )}
    </SelectionButton>
  );

  return (
    <Dialog
      open={open}
      title={`Assign a manager to "${space.name}"`}
      onClose={onClose}
    >
      {/* The dialog body is a plain column with no gap of its own, so the
          spacing between every block — the intro included — lives here. */}
      <div className="flex flex-col gap-3">
        <span className="text-fg-regular">
          A manager owns every model in this space and can grant access to it.
          They must be a member of the organization.
        </span>

        {candidates === undefined ? (
          <LoadingState
            size="small"
            label="Finding people already in this space…"
          />
        ) : (
          <>
            {candidates === null && (
              <span className="flex items-center gap-2 text-fg-muted-regular">
                Couldn&apos;t load who&apos;s already in this space.
                <Button variant="secondary" size="xs" onClick={loadCandidates}>
                  Retry
                </Button>
              </span>
            )}

            {(shortlist?.length ?? 0) > 0 && (
              <div className="flex flex-col gap-1">
                <span className="text-fg-muted-regular">
                  Already in this space
                </span>
                <div className="flex max-h-40 flex-col overflow-y-auto">
                  {shortlist?.map((candidate) =>
                    personRow(
                      candidate.client_id,
                      candidate.name,
                      candidate.email,
                      <>
                        {candidate.inherited && (
                          <span className="shrink-0 text-fg-muted-regular">
                            inherited
                          </span>
                        )}
                        <Badge label={candidate.role} />
                      </>,
                    ),
                  )}
                </div>
              </div>
            )}

            {otherMembers.length > 0 && (
              <div className="flex flex-col gap-1">
                <span className="text-fg-muted-regular">
                  {(shortlist?.length ?? 0) > 0
                    ? "Anyone else in the organization"
                    : "Members of the organization"}
                </span>
                <div className="flex max-h-40 flex-col overflow-y-auto">
                  {otherMembers.map((member) =>
                    personRow(
                      member.client_id,
                      member.display_name,
                      member.email,
                    ),
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-1">
              <span className="text-fg-muted-regular">{emailLabel}</span>
              <TextInput
                value={email}
                onChange={(value) => {
                  setEmail(value);
                  setSelected(null);
                }}
                placeholder="member@example.com"
                ariaLabel="Assign a manager by email"
              />
            </div>
          </>
        )}
      </div>

      <DialogActions
        actions={[
          { label: "Cancel", variant: "secondary", onClick: onClose },
          {
            label: "Assign manager",
            variant: "primary",
            loading: assigning,
            disabled: !selected && email.trim().length === 0,
            onClick: () => void assign(),
          },
        ]}
      />
    </Dialog>
  );
};
